import isEqual from "lodash-es/isEqual";
import { FormProvider, useForm } from "react-hook-form";

import TextLink from "components/DocumentationSnippets/TextLink";
import FormFieldSpace from "components/FormFields/Space";
import FormSourceCode from "components/Forms/SourceCode";
import { SourceCodeProjects } from "components/Forms/SourceCode/types";
import Box from "ds/components/Box";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import { AnalyticsPageModule } from "hooks/useAnalytics/pages/module";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { VcsProvider } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { INTEGRATIONS_FILTER_PATH } from "views/Account/Integrations/registry/shared/VcsShared/constants";
import useModuleAvailableSpaces from "views/Account/Module/useModuleAvailableSpaces";
import { AccountContext } from "views/AccountWrapper";

import { ModuleFormContext } from "../context";
import NewModuleFooter from "../Footer";
import { ModuleCreationWizardStep, ModuleVcsFormFields } from "../types";
import { getTooltipAnalyticsProps } from "../utils";
import Documentation from "./Documentation";

const processValuesBeforeSubmit = (values: ModuleVcsFormFields): ModuleVcsFormFields => {
  const projectRoot = values.projectRoot?.trim();

  const gitSparseCheckoutPaths = values.gitSparseCheckoutPaths
    ?.map((path) => {
      return {
        value: path.value.trim(),
      };
    })
    .filter((path) => path.value);

  return {
    ...values,
    projectRoot,
    gitSparseCheckoutPaths,
  };
};

const NewModuleVcs = () => {
  const { viewer } = useTypedContext(AccountContext);
  const availableSpaces = useModuleAvailableSpaces();

  const {
    currentStep,
    updateStepData,
    formData,
    setInternalFormData,
    setAccountOrAuthErrorContent,
    internalData,
  } = useTypedContext(ModuleFormContext);
  const stepData = formData[ModuleCreationWizardStep.Vcs];

  const builderForm = useForm<ModuleVcsFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const { watch, getValues, reset } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const newStepData = watch();
  const spaceData = getValues("space");

  const processStepData = () => {
    const preparedData = processValuesBeforeSubmit(newStepData);
    return updateStepData(currentStep, preparedData);
  };

  const resetFormFields = (type: VcsProvider) => {
    reset({
      repository: "",
      branch: "",
      projectRoot: "",
      repositoryURL: undefined,
      provider: type,
      vcsIntegrationId: undefined,
      space: spaceData,
      gitSparseCheckoutPaths: [],
    });
  };

  const isDataChanged = !isEqual(newStepData, stepData);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Connect to source code"
          description={
            <>
              {viewer?.admin && (
                <>
                  Spacelift supports multiple source code providers. You can connect one on the{" "}
                  <TextLink
                    analyticsPage={AnalyticsPageModule.ModuleNew}
                    analyticsTitle="Source code link click"
                    href={INTEGRATIONS_FILTER_PATH}
                  >
                    Source Code Integrations
                  </TextLink>{" "}
                  page.
                </>
              )}
              {!viewer?.admin && (
                <>
                  Spacelift supports multiple source code providers. <br />
                  Please reach out to one of your friendly account admins to set up one.
                </>
              )}
            </>
          }
        />

        <FormProvider {...builderForm}>
          <Box direction="column" gap="x-large">
            <FormFieldSpace
              spaces={availableSpaces}
              {...getTooltipAnalyticsProps("Details", "Space")}
            />

            <FormSourceCode
              spaceId={spaceData}
              setInternalFormData={setInternalFormData}
              provider={newStepData.provider}
              vcsIntegrationId={newStepData.vcsIntegrationId}
              repository={newStepData.repository}
              setAccountOrAuthErrorContent={setAccountOrAuthErrorContent}
              resetFormFields={resetFormFields}
              projectType={SourceCodeProjects.Module}
              useGitCheckout={internalData.useGitCheckout}
            />
          </Box>
        </FormProvider>
      </FullScreenModalBody>

      <NewModuleFooter
        isDataChanged={isDataChanged}
        processStepData={processStepData}
        // TODO module-redesign update with proper docs link
        documentationLink={getDocsUrl("/vendors/terraform/module-registry#spacelift-setup")}
        documentationTitle="Integrate source code"
        documentationBody={<Documentation />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewModuleVcs;
